import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { NAV_ITEMS } from '../../data/nav';
import type { MarketingPage } from '../../data/nav';

// Re-exported so existing imports (`from './Navigation'`) keep resolving.
export type { MarketingPage };

interface NavigationProps {
  current: MarketingPage;
}

/**
 * Hamburger nav for the marketing site — floating top-right corner.
 * Same placement on every page (see MarketingSite / SiteHeader CSS).
 */
export function Navigation({ current }: NavigationProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div className="nav-hamburger" ref={containerRef}>
      <button
        className="nav-hamburger-btn"
        aria-label="Menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="nav-hamburger-line" />
        <span className="nav-hamburger-line" />
        <span className="nav-hamburger-line" />
      </button>
      {open && (
        <nav className="nav-hamburger-menu" role="menu">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.page}
              to={item.path}
              className={`nav-hamburger-link ${current === item.page ? 'active' : ''}`}
              onClick={() => setOpen(false)}
              role="menuitem"
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}
